/**
 * Every tunable number in the simulation, in one place (02-game-design is the source).
 * R4 says these belong in content/*.json; they live here until the content loader exists
 * (M3), and nothing else in src/sim may hard-code a gameplay value.
 */

import { TICK_RATE } from './types';

export const secs = (s: number): number => Math.round(s * TICK_RATE);

// Rare gear, searchlights, the executioners and the round-two perk numbers: same rules, second file (size limit).
export * from './tuningGear';

export const WEIGHT = { magazine: 1, grenade: 2, law: 6, bomb: 4, firstAid: 2 } as const;

export const STANDARD_ISSUE = {
  magazines: 4,
  roundsInMag: 30,
  grenades: 3,
  laws: 1,
  bombs: 1,
  firstAid: 1,
} as const;

/** Standard issue on his back: 4 + 6 + 6 + 4 + 2 = 22 units (02 §8). */
const STANDARD_WEIGHT = STANDARD_ISSUE.magazines * WEIGHT.magazine + STANDARD_ISSUE.grenades * WEIGHT.grenade
  + STANDARD_ISSUE.laws * WEIGHT.law + STANDARD_ISSUE.bombs * WEIGHT.bomb + STANDARD_ISSUE.firstAid * WEIGHT.firstAid;

export const RANGER = {
  radius: 0.35,
  walkSpeed: 2.2,
  runSpeed: 4.6,
  crawlSpeed: 0.9,
  downedSpeed: 0.45,
  /** How fast the body comes round, rad/s. The aim itself is instant; the body is not. */
  turnRate: 11,
  proneTurnRate: 3.2,
  /** He can only run roughly the way he is facing: no sprinting north while shooting south. */
  runArcDeg: 70,
  /** Moving more than this far off the aim is a backpedal, facing the threat. */
  backpedalBeyondDeg: 100,
  strafeSpeed: 0.85,
  backpedalSpeed: 0.6,
  /** The weapon can only bear this far from where the body faces (02 §4). */
  fireArcStandDeg: 100,
  fireArcProneDeg: 50,
  /** Seconds of continuous running that fill the fatigue bar, unencumbered. */
  fatigueFillSeconds: 25,
  drainWalkSeconds: 40,
  drainStillSeconds: 28,
  drainProneSeconds: 22,
  woundSpeedPenalty: 0.08,
  woundFatiguePenalty: 0.3,
  desertFatiguePenalty: 0.3,
  carryCap: 30,
  /** He trains in standard issue: only what he carries beyond it slows and tires him (was 12, which penalised it). */
  freeWeight: STANDARD_WEIGHT,
  weightRunPenalty: 0.015,
  weightFatiguePenalty: 0.03,
  staggerTicks: secs(0.35),
  staggerSpeed: 0.4,
  woundChance: 0.25,
  woundChancePerRecentHit: 0.15,
  woundChanceCap: 0.7,
  recentHitWindow: secs(3),
  emplacementWoundChance: 0.4,
  bleedOutTicks: secs(45),
  captureRadius: 10,
  captureTicks: secs(3),
  firstAidTicks: secs(3),
  boardTicks: secs(1.5),
  boardRadius: 1.6,
  lootRadius: 2.6,
  /** After a run the weapon takes this long to come back up: no firing meanwhile (Snap Shot shortens it). */
  raiseTicksAfterRun: 18,
} as const;

export const CARBINE = {
  roundsPerMag: 30,
  ticksPerShot: 6,
  reloadTicks: secs(1.8),
  speed: 120,
  range: 42,
  /** Degrees of spread (half-angle) by what the ranger is doing. */
  spreadProne: 1.2,
  spreadStill: 1.8,
  spreadWalk: 4,
  spreadRun: 8,
  spreadPerWound: 0.15,
  noise: 45,
  heavyHitChance: 0.35,
} as const;

export const GRENADE = {
  minRange: 4,
  maxRange: 22,
  flightTicks: secs(0.9),
  fuseTicks: secs(1.6),
  radius: 6,
  noise: 70,
} as const;

export const LAW = { speed: 60, range: 90, armDistance: 6, radius: 5, noise: 100, cooldown: secs(1) } as const;

export const BOMB = { placeTicks: secs(2.5), radius: 9, noise: 100 } as const;

export const KNIFE = { reach: 1.35, arcDeg: 40, cooldown: secs(0.3), loudNoise: 10 } as const;

export const NOISE = { run: 12, walk: 6, crawl: 1.5, footstepEveryTicks: 20, shout: 15, enemyShot: 45, arctic: 0.7 } as const;

export const ENEMY = {
  radius: 0.4,
  walkSpeed: 1.5,
  huntSpeed: 3.2,
  fovDeg: 110,
  /** Vision range by time of day (02 §6.2, rescaled for the 36 x 28 m view). */
  range: { dawn: 17, day: 22, dusk: 17, night: 10 },
  instantRange: 5,
  senseRange: 2.2,
  detectPerSecond: 0.9,
  forgetPerSecond: 0.3,
  suspiciousAt: 0.4,
  perceptionEveryTicks: 6,
  suspiciousTicks: secs(1.5),
  lostSightTicks: secs(1.2),
  burst: 3,
  ticksPerShot: 8,
  bulletSpeed: 100,
  bulletRange: 40,
  preferredRange: 14,
  bodySightRange: 10,
  // R4: a vehicle is armour like the other machines - `SPECIALS.armourHp` replaces this the moment one exists.
  hp: { rifleman: 2, elite: 3, officer: 2, flamethrower: 3, rocketTrooper: 2, miniBunker: 99, minitank: 99,
    truck: 99, staffCar: 99, railEngine: 99, launcherTruck: 99, dog: 1 },
  downTicksMin: secs(2),
  downTicksMax: secs(4),
  emplacementBurst: 6,
  emplacementTicksPerShot: 5,
  emplacementRange: 24,
  emplacementArcDeg: 60,
} as const;

/** Values that scale with the 1-9 threat rating (02 §12): linear from `at1` to `at9`. */
export function byThreat(threat: number, at1: number, at9: number): number {
  const t = Math.min(1, Math.max(0, (threat - 1) / 8));
  return at1 + (at9 - at1) * t;
}
export const THREAT = {
  reactionSeconds: [0.9, 0.3],
  /**
   * Spread of a rifleman's aim, degrees, from threat 1 to threat 9. Measured on screen at 18 deg: one
   * rifleman still downs a standing ranger in 5-15 s at every threat, but most individual rounds visibly
   * miss and the garrison reads as harmless - "do they shoot?". 12 deg at threat 1 keeps the same time to
   * down while putting the rounds where he can see them go by. Emplacements take 0.7 of this (shooting.ts).
   */
  aimErrorDeg: [12, 6],
  searchSeconds: [30, 60],
  burstPauseSeconds: [1.3, 0.6],
} as const;

export const ALERT = {
  decayTicks: secs(90),
  sustainedCombatTicks: secs(8),
  convergeRadius: 60,
  firstWaveTicks: secs(25),
  waveEveryTicks: secs(50),
  maxWaves: 3,
  waveSize: 3,
} as const;

/** The drop (02 §3). */
export const INSERTION = {
  altitude: 60,
  leadIn: 30,
  flySpeed: 14,
  steerSpeed: 6,
  edgeMargin: 8,
  podFallSeconds: 6,
  sinkRate: 7.5,
  turnSinkPenalty: 0.6,
  glideSpeed: 5,
  turnRate: 1.57,
  badlandsMargin: 6,
  roughLandingWoundChance: 0.5,
  podReach: 1.8,
  podOpenTicks: secs(2),
} as const;

/** Squad behaviour (02 §9.4). */
export const TACTICS = {
  squadRadius: 30,
  officerAura: 15,
  officerShockRadius: 25,
  rethinkTicks: secs(1.5),
  flankRadius: 9,
  coverSearch: 11,
  runnerReach: 90,
  rallyRadius: 12,
  grenadeAfterLostTicks: secs(2),
  /** A player round passing this close makes a man duck. */
  nearMiss: 1.6,
  suppressTicks: secs(1.2),
  casualtyShockRadius: 12,
  fleeTicks: secs(9),
  /** Chance that a soldier brought to 0 hp by gunfire or fragments is incapacitated, not killed. */
  woundedChance: 0.4,
  woundedCryTicks: secs(6),
  rescueReach: 22,
  dragSpeed: 1.1,
  dragDistance: 14,
  /** Enemies stop firing at a downed ranger who has not fired for this long. */
  holdFireAfterTicks: secs(1.5),
  barkCooldown: secs(1.5),
  barkRadius: 15,
  quietBarkRadius: 9,
} as const;

/** The ranger dragging a body (02 §6.2). */
export const DRAG = { reach: 1.5, speed: 1.3, crawlSpeed: 0.55, trail: 0.95 } as const;

export const OSPREY = { inboundTicks: secs(45), hoverTicks: secs(20) } as const;

export const SCORE = {
  objective: 1000,
  partial: 250,
  extracted: 300,
  rifleman: 25,
  elite: 50,
  officer: 75,
  flamethrower: 60,
  rocketTrooper: 60,
  miniBunker: 200,
  minitank: 250,
  /** R4: a lorry is worth its load; the train and a launcher under way are the mission itself. */
  truck: 150, staffCar: 100, railEngine: 250, launcherTruck: 250, dog: 10,
  emplacement: 150,
  /** R4: the bridge dropped under the convoy that was crossing it (10 §2, mission 13). */ spanDeed: 800,
  perSecondLeft: 1,
  ghostBonus: 400,
  /** Mission 11 "leans on destruction" (03 §2): emplacements and their crews are worth this much more. */
  diversionEmplacement: 1.5,
} as const;

/** Objectives, escorts, disguise, dormant spawns (03 §2-§3, 02 §6.4, §9.1, §10, §11). Owned by src/sim/objectives. */
export const OBJECTIVES = {
  /** Interact reach from a prop's footprint (radio, lever) or to a captive; a captive this near a prison is in it. */
  useReach: 1.6, freeReach: 1.8, cellRadius: 6,
  /** A bomb this close to the target's footprint is planted; under `bombsHoldUntilMidnight` its fuse is pinned here. */
  plantRadius: 4, midnightFuseTicks: secs(3600),
  /** The officer gives up to a weapon pointed at him from here, held this long, with no alerted guard this close. */
  surrenderRange: 2, surrenderAimDeg: 25, surrenderGuardRadius: 8, surrenderTicks: secs(0.5),
  /** He bolts when left this far behind for this long; fleeing, he is gone this close to the north edge. */
  boltDistance: 10, boltTicks: secs(6), fleeEdgeMargin: 4,
  /** Escorts jog at the ranger's heels in a loose column, and ask for a real path only after walking into something. */
  captiveRadius: 0.3, followSpeed: 3.4, woundedSpeedFactor: 0.6, followGap: 1.4, followSpacing: 0.8,
  stuckTicks: secs(0.4), repathCooldownTicks: secs(1.5),
  /** A round that touches an escort wounds him this often; the second wound kills. */
  captiveWoundChance: 0.5,
  /** Under a hovering Osprey: escorts this close make for the ladder alone; the ranger waits for those this close, */
  /** a second each on the ladder - until this little hover time is left, when the crew chief waves him up anyway. */
  ladderRushRadius: 30, ladderQueueRadius: 12, captiveBoardTicks: secs(1), lastCallTicks: secs(4),
  /** `nearObjective` spawns wake this close to a target; `reinforceTarget` posts this many elites this far from it. */
  wakeRadius: 40, reinforceCount: 2, reinforceRing: 5, demolitionRadius: 7,
  /** Disguise: knife reach to a guardhouse, tent or hut wall; who looks twice, from how far, for how long. */
  interiorReach: 3, scrutinyRadius: 5, scrutinyTicks: secs(8), scrutinyHasteFactor: 2, scrutinyDecay: 0.5,
  /** Mission 11: the warning tone sounds this long before silence fails the diversion. */
  signalWarningTicks: secs(30),
  score: { noContact: 750, breach: -1000, captive: 200, woundedPrisoner: 0.5 },
} as const;

/** Ground hazards (02 §4, §9.2): mines, wire, water and ice, proximity posts. Owned by src/sim/hazards. */
export const HAZARDS = {
  mineTrigger: 0.6, mineBlast: 4, /** of a blast's radius: a grenade clears 4 m */ mineClearFraction: 0.7,
  minePopNoise: 25, mineNavCost: 40,
  wirePushTicks: secs(1), wireTangleTicks: secs(3), wireImmuneTicks: secs(0.5), wireNoise: 10, wireMaxDepth: 1,
  wadeSpeed: 0.5, wadeNoise: 1.5, iceSpeed: 0.8, crossingHalfWidth: 2.5, walkerBreakChance: 0.5,
  drownAfterTicks: secs(8), drownEveryTicks: secs(6), proximityTrigger: 3, proximityBlast: 5,
  /** Fatigue per second of wading, on top of everything else; and how long a change of direction takes on ice. */
  wadeFatiguePerSecond: 0.05, iceLagSeconds: 0.3,
} as const;

/** Special soldiers and automated defences (02 §9.1-§9.2). Owned by src/sim/enemies/specials. */
export const SPECIALS = {
  /** Armour shrugs off whatever `damageEnemy` deals; only a heavy or bomb blast this close destroys it. */
  armourHp: 9999, armourKillRadius: 2, /** the minitank's hull as drawn: tracks at +-0.79 */ tankBody: 0.8,
  /** m/s: "faster than a crawl" */ motionSpeed: 1.2,
  flameRange: 7, flameConeDeg: 30, flameWoundTicks: secs(0.75), flameTurnRate: 4, flameNoise: 25,
  flameRearArcDeg: 120, ruptureRadius: 3, ruptureFuseTicks: secs(0.2),
  rocketMinRange: 20, rocketMaxRange: 45, rocketDangerClose: 8, rocketAimTicks: secs(1.2), rocketSpeed: 30,
  /** `explode` gives 2 wounds inside 0.45 of the radius: 3 m here. */ rocketBlast: 6.7, rocketNoise: 100,
  rocketTrackTicks: secs(3), /** how far past the minimum he backs off before turning to aim */ rocketRangeMargin: 3,
  bunkerRadius: 14, bunkerLiveTicks: secs(6), bunkerDormantTicks: secs(4), bunkerBurst: 4, bunkerTicksPerShot: 5,
  bunkerPauseTicks: secs(0.6), bunkerSpreadDeg: 5, bunkerTroopHitChance: 0.4, bunkerTraverse: 6, bunkerNoise: 45,
  bunkerBody: 0.9, bunkerNavCost: 6,
  tankSpeed: 3.5, tankRadius: 0.5, tankWakeRadius: 40, tankContact: 1.1, tankBlast: 3, tankMineTrigger: 0.8,
  tankFireRange: 18, tankBurst: 3, tankTicksPerShot: 6, tankPauseTicks: secs(1.6), tankSpreadDeg: 9,
  /** Inside this it drives straight at him instead of following the grid. */ tankDirectRange: 4, tankTurnRate: 6,
  turretTraverseDeg: 45, turretAimToleranceDeg: 6,
} as const;
